import React, { useEffect, useMemo, useState } from 'react';
import { Building2, FileSignature, Truck, Users } from 'lucide-react';
import { Heading } from '../../layout';
import { PillTabBar } from '../../reusable';
import { useUIColors } from '../../../hooks/useTheme';
import {
  PERIOD_OPTIONS,
  MONTH_OPTIONS,
  getAvailableYearsFromDates,
  resolvePeriodSelection,
  periodLabelStyle,
  periodSelectStyle,
} from '../../placeholders/periodSummaryUtils';
import { getStakeholders } from '../../../services/stakeholdersAPI';
import StakeholdersPanel from './stakeholders/StakeholdersPanel';

const NAV_ITEMS = [
  { key: 'clientes', label: 'Clientes', icon: Building2 },
  { key: 'proveedores', label: 'Proveedores', icon: Truck },
  { key: 'honorarios', label: 'Prestadores de honorarios', icon: FileSignature },
  { key: 'colaboradores', label: 'Colaboradores', icon: Users },
];

const PERIOD_TITLES = {
  monthly: 'Stakeholders',
  annual: 'Stakeholders anual',
};

const YEAR_SCOPED_PERIODS = new Set(['annual', 'annual_ytd', 'quarterly', 'semestral']);

const FinanceStakeholdersWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [section, setSection] = useState('clientes');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [secondaryYear, setSecondaryYear] = useState(String(new Date().getFullYear()));
  const [secondaryMonth, setSecondaryMonth] = useState(String(new Date().getMonth()));
  const [dataByRole, setDataByRole] = useState({});
  const [errorByRole, setErrorByRole] = useState({});

  useEffect(() => {
    if (dataByRole[section]) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setErrorByRole((prev) => ({ ...prev, [section]: '' }));
        const payload = await getStakeholders(section);
        if (cancelled) return;
        setDataByRole((prev) => ({ ...prev, [section]: Array.isArray(payload) ? payload : [] }));
      } catch (err) {
        if (cancelled) return;
        setErrorByRole((prev) => ({ ...prev, [section]: err.message || 'No se pudieron cargar los stakeholders.' }));
        setDataByRole((prev) => ({ ...prev, [section]: [] }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [section, dataByRole]);

  const rows = dataByRole[section];
  const isLoading = !rows;

  const dateValues = useMemo(() => {
    const periods = new Set();
    (rows || []).forEach((row) => (row.monthly || []).forEach((bucket) => periods.add(bucket.period)));
    return Array.from(periods).map((period) => {
      const [year, month] = period.split('-').map(Number);
      return new Date(year, month - 1, 1);
    });
  }, [rows]);

  const availableYears = useMemo(() => getAvailableYearsFromDates(dateValues), [dateValues]);

  const selectedPeriod = useMemo(
    () =>
      resolvePeriodSelection({
        period: periodFilter,
        dateValues,
        secondaryYear,
        secondaryMonth,
        titles: PERIOD_TITLES,
      }),
    [periodFilter, dateValues, secondaryYear, secondaryMonth]
  );

  useEffect(() => {
    if (!availableYears.length) return;
    if (YEAR_SCOPED_PERIODS.has(periodFilter)) {
      if (secondaryYear !== 'all' && !availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear('all');
      }
      return;
    }
    if (periodFilter === 'monthly' && !availableYears.includes(Number(secondaryYear))) {
      setSecondaryYear(String(availableYears[availableYears.length - 1]));
    }
  }, [availableYears, periodFilter, secondaryYear]);

  const handlePeriodFilterChange = (nextPeriod) => {
    setPeriodFilter(nextPeriod);
    const now = new Date();
    const latestYear = availableYears[availableYears.length - 1] || now.getFullYear();
    if (YEAR_SCOPED_PERIODS.has(nextPeriod)) {
      setSecondaryYear('all');
      return;
    }
    if (nextPeriod === 'monthly') {
      setSecondaryYear(String(availableYears.includes(now.getFullYear()) ? now.getFullYear() : latestYear));
      setSecondaryMonth(String(now.getMonth()));
    }
  };

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'start',
          columnGap: '16px',
          rowGap: '12px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Heading level={2} isDarkMode={isDarkMode}>
            Stakeholders
          </Heading>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: ui.muted }}>
            Clientes, proveedores, prestadores y colaboradores consolidados por RUT desde los libros contables y sus
            fichas.
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            gap: '10px',
            flexWrap: 'nowrap',
            marginTop: '22px',
            justifySelf: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
            <span style={periodLabelStyle(ui)}>Período</span>
            <select
              value={periodFilter}
              onChange={(event) => handlePeriodFilterChange(event.target.value)}
              style={{ ...periodSelectStyle(ui), minWidth: '180px' }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {YEAR_SCOPED_PERIODS.has(periodFilter) ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
              <span style={periodLabelStyle(ui)}>Año</span>
              <select
                value={secondaryYear}
                onChange={(event) => setSecondaryYear(event.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: '110px' }}
              >
                <option value="all">Todas</option>
                {availableYears.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {periodFilter === 'monthly' ? (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Año</span>
                <select
                  value={String(
                    secondaryYear === 'all'
                      ? availableYears[availableYears.length - 1] || new Date().getFullYear()
                      : secondaryYear
                  )}
                  onChange={(event) => setSecondaryYear(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Mes</span>
                <select
                  value={secondaryMonth}
                  onChange={(event) => setSecondaryMonth(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
        </div>
      </div>

      <PillTabBar
        ariaLabel="Tipos de stakeholder"
        items={NAV_ITEMS.map((item) => ({
          ...item,
          badge: dataByRole[item.key]?.length,
        }))}
        activeKey={section}
        onChange={setSection}
        style={{ marginBottom: 16 }}
      />

      <StakeholdersPanel
        key={section}
        ui={ui}
        isDarkMode={isDarkMode}
        role={section}
        rows={rows || []}
        loading={isLoading}
        error={errorByRole[section] || ''}
        selectedPeriod={selectedPeriod}
      />
    </div>
  );
};

export default FinanceStakeholdersWorkspace;
